/**
 * Landmarks3D — GTA-style stylized Tucson landmarks rendered as a Mapbox
 * custom layer using Three.js. Shares the Mapbox GL context (no second
 * canvas), so landmarks composite cleanly with terrain and 3D buildings.
 *
 * v1 landmark: A-Mountain (Sentinel Peak) — the iconic peak west of downtown
 * with the giant whitewashed "A" on its east face (UA tradition since 1915).
 *
 * Design philosophy: GTA's landmarks aren't photoreal — they're stylized,
 * recognizable, slightly oversized, with floating block-letter labels. The
 * goal is "you can tell where you are at a glance," not "looks like a photo."
 *
 * Public API:
 *   mountLandmarks(map)   — add the landmark layer; idempotent
 *   dismountLandmarks(map)
 *
 * Adding a new landmark: write a builder fn that returns { group, lngLat },
 * register it in LANDMARKS, ship.
 */

import * as THREE from 'three';
import mapboxgl from 'mapbox-gl';

const LAYER_ID = 'tpd-landmarks-3d';

interface LandmarkInstance {
  group:  THREE.Group;
  lngLat: [number, number];
  /** Per-frame tick for animations (label bob, glow throb). t = seconds since mount. */
  update?: (elapsed: number) => void;
}

type LandmarkBuilder = () => LandmarkInstance;

// ────────────────────────────────────────────────────────────────────────────
// Label sprite — GTA-style block label floating above a landmark
// ────────────────────────────────────────────────────────────────────────────

function makeLabelSprite(
  text: string,
  opts: { color?: string; bgAlpha?: number; widthPx?: number } = {},
): THREE.Sprite {
  const color   = opts.color   ?? '#ffe88a';
  const bgAlpha = opts.bgAlpha ?? 0;
  const widthPx = opts.widthPx ?? 1024;

  const canvas = document.createElement('canvas');
  canvas.width  = widthPx;
  canvas.height = widthPx / 4;
  const ctx = canvas.getContext('2d')!;

  if (bgAlpha > 0) {
    ctx.fillStyle = `rgba(8, 12, 22, ${bgAlpha})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.font         = `bold ${Math.floor(canvas.height * 0.75)}px "Arial Black", "Helvetica", sans-serif`;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  // Heavy outline for readability over any basemap
  ctx.lineWidth   = canvas.height * 0.08;
  ctx.strokeStyle = '#000000';
  ctx.strokeText(text, canvas.width / 2, canvas.height / 2);
  ctx.fillStyle   = color;
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest:   false,   // always visible above geometry
    depthWrite:  false,
  });
  const sprite = new THREE.Sprite(material);
  // Aspect ratio matches the canvas (4:1)
  sprite.scale.set(60, 15, 1);
  return sprite;
}

// ────────────────────────────────────────────────────────────────────────────
// A-Mountain (Sentinel Peak) — the headliner
// ────────────────────────────────────────────────────────────────────────────
// Real-world coords: 32.21225° N, -110.99311° W.
// Peak rises ~76m above the valley floor. The big white "A" on the east face
// is ~50ft (~15m) tall, painted on a stone outline.
//
// Stylization choices (deliberately not realistic):
//   - Slightly oversized vertical exaggeration (≈1.4×) so the peak reads from
//     a long distance on the map, GTA-style
//   - 6-sided low-poly cone with vertex jitter for a "rocky" silhouette
//   - Warm sandstone color, deeper shadows in crevices via vertex colors
//   - Giant flat-white "A" billboard on the east face, slightly emissive so it
//     glows softly at night
//   - Floating "A-MOUNTAIN" label sprite ~30m above the peak

function buildAMountain(): LandmarkInstance {
  const group = new THREE.Group();

  const peakHeight = 110; // exaggerated for visibility
  const baseRadius = 130;
  const peakRadius = 8;

  // Low-poly peak — cone with vertex jitter for rocky silhouette
  const peakGeo = new THREE.ConeGeometry(baseRadius, peakHeight, 7, 3, false);
  // Jitter the position attribute for a more naturalistic, less-clean cone
  const pos = peakGeo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    // Only jitter mid/lower vertices, leave the apex sharp
    const distFromApex = (peakHeight / 2) - y;
    const jitter = Math.min(distFromApex / peakHeight, 1) * 18;
    pos.setX(i, x + (Math.random() - 0.5) * jitter);
    pos.setZ(i, z + (Math.random() - 0.5) * jitter);
    pos.setY(i, y + (Math.random() - 0.5) * jitter * 0.4);
  }
  pos.needsUpdate = true;
  peakGeo.computeVertexNormals();

  const peakMat = new THREE.MeshStandardMaterial({
    color:     0xb58c5a, // warm sandstone
    roughness: 0.95,
    metalness: 0.05,
    flatShading: true,
  });
  const peak = new THREE.Mesh(peakGeo, peakMat);
  peak.position.y = peakHeight / 2;
  group.add(peak);

  // Darker rock skirt at the base for "shadowed crevice" reading
  const skirtGeo = new THREE.ConeGeometry(baseRadius * 1.05, peakHeight * 0.35, 7, 1, false);
  const skirtMat = new THREE.MeshStandardMaterial({
    color: 0x6b4a2a, roughness: 1, flatShading: true,
  });
  const skirt = new THREE.Mesh(skirtGeo, skirtMat);
  skirt.position.y = peakHeight * 0.175;
  group.add(skirt);

  // The "A" — flat white quad on the east face (east = +X in local coords
  // after our scene rotation; Mapbox MercatorCoordinate puts +X eastward at
  // this latitude after the makeRotationX(PI/2) we apply below).
  const aCanvas = document.createElement('canvas');
  aCanvas.width = 256;
  aCanvas.height = 256;
  const actx = aCanvas.getContext('2d')!;
  actx.clearRect(0, 0, 256, 256);
  actx.fillStyle = '#ffffff';
  actx.font = 'bold 230px "Arial Black", sans-serif';
  actx.textAlign = 'center';
  actx.textBaseline = 'middle';
  actx.strokeStyle = '#1a0d05';
  actx.lineWidth = 10;
  actx.strokeText('A', 128, 138);
  actx.fillText  ('A', 128, 138);
  const aTex = new THREE.CanvasTexture(aCanvas);
  aTex.minFilter = THREE.LinearFilter;
  const aMat = new THREE.MeshStandardMaterial({
    map:               aTex,
    transparent:       true,
    emissive:          0xffffff,
    emissiveMap:       aTex,
    emissiveIntensity: 0.45, // softly glows at night
    side:              THREE.DoubleSide,
    depthWrite:        false,
  });
  const aGeo  = new THREE.PlaneGeometry(45, 45);
  const aMesh = new THREE.Mesh(aGeo, aMat);
  // Place on east face, ~60% up the peak. ConeGeometry centered at y=0 with
  // radius 130 at base, 8 at top. At ~60% height (y ≈ 22 in local cone coords,
  // so y ≈ peakHeight/2 - 22 in world), the radius is ~50.
  aMesh.position.set(50, peakHeight * 0.55, 0);
  // Face east (+X), tilted slightly to match the slope of the mountainside
  aMesh.rotation.y = -Math.PI / 2;
  aMesh.rotation.x = THREE.MathUtils.degToRad(-12); // tilt back to follow slope
  group.add(aMesh);

  // Floating GTA-style label above the peak
  const label = makeLabelSprite('A-MOUNTAIN', { color: '#ffe88a' });
  label.position.set(0, peakHeight + 35, 0);
  group.add(label);

  // Soft warm rim light from the west (afternoon sun direction) so the peak
  // catches a bronze edge on east face — accents the A
  const rim = new THREE.DirectionalLight(0xffb070, 0.9);
  rim.position.set(-1, 0.5, 0);
  group.add(rim);

  return {
    group,
    lngLat: [-110.99311, 32.21225],
    update: (t) => {
      // Label bob — gentle vertical drift so it doesn't look pasted on
      label.position.y = peakHeight + 35 + Math.sin(t * 0.9) * 2.5;
      // A glow pulse — extra-subtle, just enough to feel alive at night
      aMat.emissiveIntensity = 0.4 + 0.12 * Math.sin(t * 1.6);
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Santa Catalina Mountains — the headliner of Tucson's northern skyline
// ────────────────────────────────────────────────────────────────────────────
// The range runs roughly east-west north of the city, ~26km in length.
// Landmark peaks west→east: Pusch Ridge → Mt. Kimball → Thimble Peak →
// Cathedral Rock → Mt. Lemmon (9,159 ft, highest). Mt. Lemmon is a "sky
// island" with pine forest on top and gets snow in winter.
//
// Stylization choices:
//   - Single landmark instance centered roughly at the range midpoint
//   - 6-peak ridgeline rendered in local coords running ±10km east-west
//   - Heights exaggerated (~1.5×) for map visibility, like A-Mountain
//   - Granite-gray base color shifting cooler toward the peaks
//   - Mt. Lemmon (eastmost, tallest) gets: pine forest crown (clustered dark
//     cones) + a white snow cap (subtle, always-on so the sky-island reads
//     even in summer)
//   - Big "CATALINAS" label centered over the range
//   - Smaller "MT LEMMON" label over the highest peak

function buildCatalinas(): LandmarkInstance {
  const group = new THREE.Group();

  // Peak definitions: [name, eastOffsetMeters, peakHeight, baseRadius]
  // East offset is measured from the landmark center; negative = west.
  const peaks: { name: string; x: number; h: number; r: number; lemmon?: boolean }[] = [
    { name: 'PUSCH RIDGE',    x: -11500, h: 220, r: 1800 },
    { name: 'MT KIMBALL',     x:  -6000, h: 280, r: 2000 },
    { name: 'THIMBLE PEAK',   x:  -2500, h: 230, r: 1500 },
    { name: 'CATHEDRAL ROCK', x:   1500, h: 250, r: 1700 },
    { name: 'WINDOW ROCK',    x:   5500, h: 270, r: 1900 },
    { name: 'MT LEMMON',      x:  10500, h: 360, r: 2400, lemmon: true },
  ];

  // Shared materials — granite gray for upper rock, warm tan for foothills
  const upperMat = new THREE.MeshStandardMaterial({
    color:       0x7d8a99,
    roughness:   0.95,
    metalness:   0.05,
    flatShading: true,
  });
  const lowerMat = new THREE.MeshStandardMaterial({
    color:       0x9c7e58,
    roughness:   1,
    flatShading: true,
  });

  for (const p of peaks) {
    // Main peak cone with vertex jitter for rocky silhouette
    const peakGeo = new THREE.ConeGeometry(p.r, p.h, 7, 3, false);
    const pos = peakGeo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      const distFromApex = (p.h / 2) - y;
      const jitter = Math.min(distFromApex / p.h, 1) * (p.r * 0.12);
      pos.setX(i, pos.getX(i) + (Math.random() - 0.5) * jitter);
      pos.setZ(i, pos.getZ(i) + (Math.random() - 0.5) * jitter);
      pos.setY(i, y + (Math.random() - 0.5) * jitter * 0.4);
    }
    pos.needsUpdate = true;
    peakGeo.computeVertexNormals();
    const peakMesh = new THREE.Mesh(peakGeo, upperMat);
    peakMesh.position.set(p.x, p.h / 2, 0);
    group.add(peakMesh);

    // Foothill skirt — warmer tan, slightly wider, fraction of the peak height
    const skirtGeo = new THREE.ConeGeometry(p.r * 1.15, p.h * 0.45, 7, 1, false);
    const skirt = new THREE.Mesh(skirtGeo, lowerMat);
    skirt.position.set(p.x, p.h * 0.225, 0);
    group.add(skirt);

    if (p.lemmon) {
      // Snow cap — small white cone sitting on top, always-on so the sky
      // island reads year-round (real Lemmon only has snow in winter, but
      // the icon should always read as "the snowy one")
      const snowGeo = new THREE.ConeGeometry(p.r * 0.35, p.h * 0.18, 8, 1, false);
      const snowMat = new THREE.MeshStandardMaterial({
        color: 0xffffff, roughness: 0.4, metalness: 0.1, emissive: 0xffffff, emissiveIntensity: 0.08,
      });
      const snow = new THREE.Mesh(snowGeo, snowMat);
      snow.position.set(p.x, p.h + (p.h * 0.18) / 2 - 4, 0);
      group.add(snow);

      // Pine forest crown — cluster of small dark green cones below the
      // snow cap on the south face (toward viewer / Tucson)
      const pineMat = new THREE.MeshStandardMaterial({
        color: 0x1f3a26, roughness: 0.9, flatShading: true,
      });
      for (let i = 0; i < 18; i++) {
        const angle = (Math.random() - 0.5) * Math.PI * 0.6; // south-facing 108deg arc
        const radial = p.r * 0.4 * (0.55 + Math.random() * 0.45);
        const tx = p.x + Math.cos(angle) * radial * 0.3;
        const tz = Math.sin(angle) * radial;
        const ty = p.h * (0.55 + Math.random() * 0.25);
        const pine = new THREE.Mesh(
          new THREE.ConeGeometry(35 + Math.random() * 25, 80 + Math.random() * 40, 6),
          pineMat,
        );
        pine.position.set(tx, ty, tz);
        group.add(pine);
      }

      // "MT LEMMON" sub-label above the snow cap
      const lemmonLabel = makeLabelSprite('MT LEMMON', { color: '#ffffff', widthPx: 720 });
      lemmonLabel.scale.set(45, 11.25, 1);
      lemmonLabel.position.set(p.x, p.h + 90, 0);
      group.add(lemmonLabel);
    }
  }

  // Big "CATALINAS" range label, centered above the range
  const rangeLabel = makeLabelSprite('CATALINAS', { color: '#ffe88a', widthPx: 1280 });
  rangeLabel.scale.set(140, 35, 1);
  rangeLabel.position.set(0, 520, 0);
  group.add(rangeLabel);

  // Warm afternoon rim light from the southwest — catches the south face,
  // which is what the city sees
  const rim = new THREE.DirectionalLight(0xffd1a0, 1.1);
  rim.position.set(-1, 0.6, -1);
  group.add(rim);

  // Cool sky bounce from above for the granite tone
  const sky = new THREE.DirectionalLight(0x88a8c8, 0.4);
  sky.position.set(0, 1, 0.3);
  group.add(sky);

  return {
    group,
    // Center the range north of downtown Tucson, roughly middle of the
    // visible east-west span from the city.
    lngLat: [-110.880, 32.405],
    update: (t) => {
      // Range label bobs subtly so it doesn't feel pinned to the ground
      rangeLabel.position.y = 520 + Math.sin(t * 0.55) * 4;
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Landmark registry — add new builders here as they ship
// ────────────────────────────────────────────────────────────────────────────

const LANDMARKS: LandmarkBuilder[] = [
  buildAMountain,
  buildCatalinas,
  // TODO: buildBankOneTower, buildHotelCongress, buildSaguaroSentinels
];

// ────────────────────────────────────────────────────────────────────────────
// Mapbox custom layer plumbing
// ────────────────────────────────────────────────────────────────────────────

interface LandmarkSceneState {
  scene:     THREE.Scene;
  camera:    THREE.Camera;
  renderer:  THREE.WebGLRenderer;
  instances: { instance: LandmarkInstance; modelMatrix: THREE.Matrix4 }[];
  startedAt: number;
}

let state: LandmarkSceneState | null = null;

function modelMatrixForLngLat(lng: number, lat: number): THREE.Matrix4 {
  const merc = mapboxgl.MercatorCoordinate.fromLngLat([lng, lat], 0);
  const meterScale = merc.meterInMercatorCoordinateUnits();
  return new THREE.Matrix4()
    .makeTranslation(merc.x, merc.y, merc.z || 0)
    .scale(new THREE.Vector3(meterScale, -meterScale, meterScale))
    .multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));
}

export function mountLandmarks(map: mapboxgl.Map) {
  if (!map.getStyle()) return;
  if (map.getLayer(LAYER_ID)) return; // already mounted

  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 0.45));
  scene.add(new THREE.HemisphereLight(0xcce4ff, 0x331100, 0.55));

  // Each landmark sits in its own subscene-equivalent: a group at the origin,
  // composed into the main scene. We track each builder's model matrix so the
  // render loop can swap camera projection per landmark.
  const instances: { instance: LandmarkInstance; modelMatrix: THREE.Matrix4 }[] = [];
  for (const builder of LANDMARKS) {
    const instance = builder();
    const matrix   = modelMatrixForLngLat(instance.lngLat[0], instance.lngLat[1]);
    instances.push({ instance, modelMatrix: matrix });
  }

  const camera = new THREE.Camera();
  const renderer = new THREE.WebGLRenderer({
    canvas:    map.getCanvas(),
    context:   map.painter.context.gl,
    antialias: true,
  });
  renderer.autoClear = false;

  state = { scene, camera, renderer, instances, startedAt: performance.now() };

  const layer: mapboxgl.CustomLayerInterface = {
    id:             LAYER_ID,
    type:           'custom',
    renderingMode:  '3d',
    onAdd: () => {},
    render: (_gl, matrix) => {
      if (!state) return;
      const elapsed = (performance.now() - state.startedAt) / 1000;
      state.renderer.resetState();

      // Render each landmark with its own model matrix so each is anchored at
      // its real lng/lat. The scene gets re-used (children swapped) per
      // landmark to keep allocations down.
      for (const { instance, modelMatrix } of state.instances) {
        instance.update?.(elapsed);

        // Swap scene children: only this landmark + ambient/hemi visible
        state.scene.children = state.scene.children.filter(c => c.type !== 'Group');
        state.scene.add(instance.group);

        const m = new THREE.Matrix4().fromArray(matrix);
        state.camera.projectionMatrix = m.multiply(modelMatrix);
        state.renderer.render(state.scene, state.camera);
      }
      map.triggerRepaint();
    },
  };
  map.addLayer(layer);
}

export function dismountLandmarks(map: mapboxgl.Map) {
  if (map.getLayer(LAYER_ID)) {
    try { map.removeLayer(LAYER_ID); } catch { /* nbd */ }
  }
  if (state) {
    for (const { instance } of state.instances) {
      instance.group.traverse((o) => {
        const mesh = o as THREE.Mesh;
        mesh.geometry?.dispose?.();
        const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(m)) m.forEach(mat => mat.dispose());
        else m?.dispose?.();
      });
    }
    state = null;
  }
}
